/**
 * SuperBrain Admin Feedback Viewer
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminFeedback.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminFeedback = ({ onLogout }) => {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, new, reviewed, resolved

  useEffect(() => {
    loadFeedback();
    loadStats();
  }, []);

  const loadFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/feedback/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(response.data.feedback);
      setLoading(false);
    } catch (err) {
      setError('Failed to load feedback. Admin access required.');
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/feedback/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/feedback/${feedbackId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadFeedback();
      loadStats();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadFeedback();
      loadStats();
    } catch (err) {
      alert('Failed to delete feedback');
    }
  };

  const filteredFeedback = feedback.filter(item => {
    if (filter === 'all') return true;
    return item.status === filter;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case 'comment': return '#4A90E2';
      case 'improvement': return '#7ED321';
      case 'bug': return '#D0021B';
      default: return '#999';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: '#FFD700',
      reviewed: '#4A90E2',
      resolved: '#7ED321'
    };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: colors[status],
        color: '#fff',
        fontSize: '0.85rem',
        fontWeight: 'bold'
      }}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) return <div className="admin-feedback-loading">Loading feedback...</div>;
  if (error) return <div className="admin-feedback-error">{error}</div>;

  return (
    <div className="admin-feedback-container">
      <div className="admin-feedback-header">
        <div>
          <h1>📬 User Feedback & Comments</h1>
          <p className="admin-subtitle">© 2026 A-1 Affiliate Marketing LLC - Admin Only</p>
        </div>
        <button onClick={onLogout} className="btn-logout">Logout</button>
      </div>

      {stats && (
        <div className="feedback-stats">
          <div className="stat-card">
            <div className="stat-number">{stats.total_feedback}</div>
            <div className="stat-label">Total Feedback</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.by_status?.new || 0}</div>
            <div className="stat-label">New</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.by_type?.comment || 0}</div>
            <div className="stat-label">Comments</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.by_type?.improvement || 0}</div>
            <div className="stat-label">Improvements</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.by_type?.bug || 0}</div>
            <div className="stat-label">Bugs</div>
          </div>
        </div>
      )}

      <div className="feedback-filters">
        <button 
          className={filter === 'all' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('all')}
        >
          All ({feedback.length})
        </button>
        <button 
          className={filter === 'new' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('new')}
        >
          New ({feedback.filter(f => f.status === 'new').length})
        </button>
        <button 
          className={filter === 'reviewed' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('reviewed')}
        >
          Reviewed ({feedback.filter(f => f.status === 'reviewed').length})
        </button>
        <button 
          className={filter === 'resolved' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('resolved')}
        >
          Resolved ({feedback.filter(f => f.status === 'resolved').length})
        </button>
      </div>

      <div className="feedback-list">
        {filteredFeedback.length === 0 ? (
          <div className="no-feedback">No feedback yet. Users will submit their thoughts here.</div>
        ) : (
          filteredFeedback.map(item => (
            <div key={item.id} className="feedback-card">
              <div className="feedback-header-row">
                <div className="feedback-user-info">
                  <strong>{item.user_email}</strong>
                  <span className="user-plan">({item.user_plan})</span>
                </div>
                <div className="feedback-meta">
                  <span 
                    className="feedback-type-badge"
                    style={{ backgroundColor: getTypeColor(item.type) }}
                  >
                    {item.type.toUpperCase()}
                  </span>
                  {getStatusBadge(item.status)}
                </div>
              </div>

              <div className="feedback-message">
                {item.message}
              </div>

              <div className="feedback-timestamp">
                {new Date(item.timestamp).toLocaleString()}
              </div>

              <div className="feedback-actions">
                <select 
                  value={item.status}
                  onChange={(e) => updateStatus(item.id, e.target.value)}
                  className="status-dropdown"
                >
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                </select>
                <button 
                  onClick={() => deleteFeedback(item.id)}
                  className="btn-delete"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminFeedback;
